import ViewViewModel from "@/ViewModels/ViewViewModel";
import ComponentManager from "@/Management/ComponentManager";
import NavigationItemViewModel from "@/Components/CoreFrameNavigation/NavigationItem/NavigationItemViewModel";
import ImageViewModel from "@/ViewModels/Controls/ImageViewModel";
import TextBoxViewModel from "@/ViewModels/Controls/TextBoxViewModel";
import { NavigationViewModelConstructorParameter } from "#/CoreFrameInterfaces";
import RADApplication from "@/Management/RADApplication";
import { INavigationNodeDTO } from "coreframetypings/dto";
import LinkViewModel from "@/ViewModels/Controls/LinkViewModel";
import LabelViewModel from "@/ViewModels/Controls/LabelViewModel";
import Utilities from "@/Utility/Utilities";
import NavigationService from "@/Management/NavigationService";
import NavigationHelper from "@/Management/NavigationHelper";
import ResourceManager from "@/Management/ResourceManager";
import GlobalConfiguration from "@/Management/GlobalConfiguration";
import Messenger from "@/Utility/Messenger";
import LocatorHelper from "@/Utility/LocatorHelper";

export default class CoreFrameNavigationViewViewModel extends ViewViewModel {
    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModel>('TextBoxViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
    private NavigationItemViewModel = ComponentManager.getViewModel<typeof NavigationItemViewModel>('NavigationItemViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModel>('LinkViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

    public searchImage: ImageViewModel;
    public clearInputImage: ImageViewModel;
    public searchBox: TextBoxViewModel;
    public isNavigationExpanded: KnockoutObservable<boolean>;

    private wasSearchActive: boolean = false;

    private readonly CUSTOM_ACTION = "CUSTOM_ACTION";
    private readonly expandIcon: ImageViewModel;
    private readonly moduleIconCache: Record<string, ImageViewModel>;
    private readonly navigationNodes: KnockoutObservableArray<NavigationItemViewModel>;
    private readonly currentlySelected: KnockoutObservable<NavigationItemViewModel>;

    constructor(params: NavigationViewModelConstructorParameter) {
        super(params);

        this.expandIcon = new ImageViewModel({
            source: 'navigation-item-expand.svg',
            width: '13',
            height: '18'
        });

        this.navigationNodes = this.observableOrDefault([]) as KnockoutObservableArray<NavigationItemViewModel>;
        this.moduleIconCache = {};
        this.isNavigationExpanded = this.observableOrDefault(!Utilities.isMobileView());

        this.currentlySelected = this.observableOrDefault(null);

        this.buildNavigationTree(this.getUserNavigationTree());

        this.searchBox = this.createSearchTextBoxViewModel();
        this.searchImage = this.createSearchBoxImageViewModel();
        this.clearInputImage = this.createClearInputImageViewModel();
        
        this.registerForPrincipalChangedMessage();
    }

    public collapseWholeTree() {
        this.navigationNodes()
            .forEach(node => this.toggleExpandedState(node, false))
    }

    public restoreTreeState() {
        this.navigationNodes()
            .forEach(node => this.restoreState(node))
    }

    protected createSearchBoxImageViewModel(): ImageViewModel {
        return new ImageViewModel({
            source: 'magnifying_glass.svg'
        });
    }

    protected createClearInputImageViewModel(): ImageViewModel {
        return new ImageViewModel({
            source: 'delete.svg',
            isVisible: this.defineComputed(() => {
                const value = this.searchBox.value();
                return value?.length > 0;
            })
        });
    }

    protected createSearchTextBoxViewModel(): TextBoxViewModel {
        return new this.TextBoxViewModel({
            isEnabled: this.isNavigationExpanded,
            onChange: _.debounce(this.onSearchChanges.bind(this), 250),
            placeholder: ResourceManager.getInstance().get('NAVIGATION_SEARCHFIELD_PLACEHOLDER'),
            ariaLabel: ResourceManager.getInstance().get('NAVIGATION_SEARCHFIELD_LABEL'),
            locator: LocatorHelper.buildLocator('navigation', '_root_', 'search_input')
        });
    }

    private onSearchChanges(searchTerm: string) {
        if (Utilities.isNullOrUndefinedOrBlank(searchTerm)) {
            this.navigationNodes().forEach((nav) => {
                this.toggleNodeVisibility(nav, true);

                if (this.wasSearchActive) 
                    this.hardResetNodeStates(nav);
            });

            return;
        }

        this.navigationNodes().forEach(nav => this.toggleNodeVisibility(nav, true));

        // wir lassen das letzte Item aus, da dies den Button für die Einstellungen betrifft und wir nur in den Modulen selbst suchen wollen
        const searchableNodes = this.navigationNodes().slice(0, -1);
        this.searchForRelevantNodes(searchableNodes, searchTerm.trim().toLowerCase());

        this.navigationNodes().forEach(nav => this.toggleExpandedState(nav, true));
        this.wasSearchActive = true;
    }
    
    private hardResetNodeStates(node: NavigationItemViewModel) {
        this.traverseNavigationTree(node, (navigationItem) => {
            navigationItem.isNodeExpanded(false);
            navigationItem.recentExpandedState(false);
        });
    }

    private searchForRelevantNodes(nodes: NavigationItemViewModel[], searchTerm: string): NavigationItemViewModel[] {
        return nodes.map(node => {
            const childNodes = node.childNodes();
            const filteredChildren = childNodes
                ? this.searchForRelevantNodes(childNodes, searchTerm)
                : []

            const matchSelf = node.linkViewModel.label.caption()
                .trim()
                .toLowerCase()
                .includes(searchTerm);

            if (!matchSelf && filteredChildren.length === 0) {
                node.recentExpandedState(false);
                node.isVisible(false)
                return null;
            }
            node.recentExpandedState(true);
            return node;
        }).filter(x => x !== null);
    }

    private buildNavigationTree(rootNode: INavigationNodeDTO) {
        if (rootNode == null)
            return;

        const result: Array<NavigationItemViewModel> = [];
        for (let i = 0; i < rootNode.childNodes.length; i++) {
            const currNode = rootNode.childNodes[i];
            result.push(this.buildNavigationItem(currNode, this.locator));
        }

        const settingsItem = new this.NavigationItemViewModel({
            linkViewModel: new this.LinkViewModel({
                image: new this.ImageViewModel({
                    source: GlobalConfiguration.inlineImages.GEARWHEELS
                }),
                label: new this.LabelViewModel({
                    ergName: 'OPTIONS_LANDSCAPE_TITLE',
                    tryToLoad: false,
                })
            }),
            hasAction: true
        });

        settingsItem.onItemClick = () => {
            this.handleItemClick(settingsItem, () => {
                NavigationService.getInstance().navigateToUrl('Settings', null, null, null, null, false);
            });
        }

        result.push(settingsItem);

        this.navigationNodes(result);
    }

    private selectItem(item: NavigationItemViewModel) {
        this.currentlySelected()?.isSelected(false);
        this.currentlySelected(item);
        item.isSelected(true);
    }

    private handleItemClick(item: NavigationItemViewModel, action: () => void) {
        if (!this.isNavigationExpanded() || !item.hasAction())
            return;

        this.selectItem(item);

        action();
    }

    private restoreState(node: NavigationItemViewModel) {
        this.traverseNavigationTree(node, (navigationItem) => {
            const recentState = navigationItem.recentExpandedState();
            if (!Utilities.isNullOrUndefined(recentState)) {
                navigationItem.isNodeExpanded(recentState)
                return;
            }

            navigationItem.isNodeExpanded(false);
        });
    }

    private traverseNavigationTree(node: NavigationItemViewModel, action: (navigationItem: NavigationItemViewModel) => void): void {
        if (Utilities.isNullOrUndefined(node))
            return;

        action(node);

        const children = node.childNodes();
        for (let i = 0; i < children.length; i++)
            this.traverseNavigationTree(children[i], action);
    }

    private toggleNodeVisibility(node: NavigationItemViewModel, isVisible: boolean): void {
        this.traverseNavigationTree(node, (navigationItem) => navigationItem.isVisible(isVisible));
    }

    private toggleExpandedState(node: NavigationItemViewModel, isExpanded: boolean): void {
        this.traverseNavigationTree(node, (navigationItem) => {
            navigationItem.isNodeExpanded(isExpanded);
        });
    }

    private buildNavigationItem(node: INavigationNodeDTO, parentLocator: KnockoutObservable<string>): NavigationItemViewModel {
        const itemLocator = '[' + node.name + ']';
        const locator = this.defineComputed(() => {
            return parentLocator() + '.' + itemLocator;
        });

        const moduleIconUrl = node.iconUrl;
        if (!Utilities.isNullOrUndefinedOrBlank(moduleIconUrl) && this.moduleIconCache[moduleIconUrl] === undefined) {
            this.moduleIconCache[moduleIconUrl] = new this.ImageViewModel({
                source: node.iconUrl,
                width: '18',
                height: '18',
                parentLocator: parentLocator,
                locator: itemLocator + '.link.image',
            });
        }

        const childNodes = this.createChildItems(node.childNodes, locator);
        const hasAction = this.isAnyActionPresent(node);
        const navigationItem: NavigationItemViewModel = new this.NavigationItemViewModel({
            parentLocator: parentLocator,
            locator: itemLocator,
            isNodeExpanded: node.isExpanded,
            linkViewModel: new this.LinkViewModel({
                parentLocator: parentLocator,
                locator: itemLocator + '.link',
                image: this.moduleIconCache[node.iconUrl],
                label: new this.LabelViewModel({
                    ergName: node.ergName,
                    caption: node.caption,
                    tryToLoad: false,
                    parentLocator: parentLocator,
                    locator: itemLocator + '.link.label',
                })
            }),
            childNodes: childNodes,
            hasAction: hasAction,
            expandIcon: this.expandIcon
        });

        navigationItem.onItemClick = () => {
            this.handleItemClick(navigationItem, () => {
                if (node.action === this.CUSTOM_ACTION) {
                    node.actionCallback();
                } else if (!String.isNullOrEmpty(node.favoriteAddress)) {
                    NavigationService.getInstance().navigateToUrl(node.favoriteAddress);
                } else if (!String.isNullOrEmpty(node.action)) {
                    NavigationService.getInstance().loadViewFactory(node.action);
                } else if (!String.isNullOrEmpty(node.url)) {
                    NavigationService.getInstance().navigateToUrl(node.url);
                } else if (node.isListView === true) {
                    NavigationHelper.openListViewById(+node.viewPDOID);
                } else if (!String.isNullOrEmpty(node.pdTypeName)) {
                    NavigationHelper.openDetailView('EDIT', node.pdTypeName);
                } else if (!String.isNullOrEmpty(node.viewExtensionFactory)) {
                    NavigationHelper.openExtensionView(node.viewExtensionFactory, node.ergName);
                }
            });
        }

        return navigationItem;
    }

    private isAnyActionPresent(node: INavigationNodeDTO): boolean {
        return node.action === this.CUSTOM_ACTION
            || !String.isNullOrEmpty(node.favoriteAddress)
            || !String.isNullOrEmpty(node.action)
            || !String.isNullOrEmpty(node.url)
            || node.isListView
            || !String.isNullOrEmpty(node.pdTypeName)
            || !String.isNullOrEmpty(node.viewExtensionFactory);
    }

    private createChildItems(childNodes: INavigationNodeDTO[], parentLocator: KnockoutObservable<string>): NavigationItemViewModel[] {
        let navItems: NavigationItemViewModel[] = [];

        if (childNodes === null)
            return navItems;

        for (let i = 0; i < childNodes.length; i++) {
            let item = this.buildNavigationItem(childNodes[i], parentLocator);
            navItems.push(item);
        }

        return navItems;
    }
    
    private registerForPrincipalChangedMessage() {
        Messenger.registerForMessageId('PrincipalChangedMessage', (_) => {
            const expandedNodeKeys = this.collectExpandedNodeKeys();
            this.buildNavigationTree(this.getUserNavigationTree());
            this.restoreExpandedNodes(expandedNodeKeys);
        }, this);
    }

    private collectExpandedNodeKeys(): Set<string> {
        const expandedNodeKeys = new Set<string>();
        this.navigationNodes().forEach(node =>
            this.traverseNavigationTree(node, (navigationItem) => {
                if (navigationItem.isNodeExpanded())
                    expandedNodeKeys.add(navigationItem.locator());
            }));

        return expandedNodeKeys;
    }

    private restoreExpandedNodes(expandedNodeKeys: Set<string>): void {
        this.navigationNodes().forEach(node =>
            this.traverseNavigationTree(node, (navigationItem) => {
                const isExpanded = navigationItem.isNodeExpandable()
                    && expandedNodeKeys.has(navigationItem.locator());
                navigationItem.isNodeExpanded(isExpanded);
                navigationItem.recentExpandedState(isExpanded);
            }));
    }
    
    private unregisterForPrincipalChangedMessage() {
        Messenger.unregisterForMessageId('PrincipalChangedMessage', null, this);
    }

    private getUserNavigationTree(): INavigationNodeDTO {
        const menus = RADApplication.getInstance().user.menus();
        return (menus?.userNavigationTree as INavigationNodeDTO) ?? null;
    }

    protected disposeInternal() {
        this.unregisterForPrincipalChangedMessage();
        super.disposeInternal();
    }
}

ComponentManager.setViewModel('NavigationViewModel', CoreFrameNavigationViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');